import React, { useMemo } from 'react';
import clsx from 'clsx';
import styles from './Avatar.module.scss';

export type AvatarSize = 32 | 48 | 72;

export interface IAvatarProps {
  size?: AvatarSize;
  src?: string | null;
  alt?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  className?: string;
}

function initialsFromName(
  firstName?: string,
  lastName?: string,
  name?: string,
): string {
  const first = firstName?.trim()?.[0];
  const last = lastName?.trim()?.[0];
  if (first || last) {
    return `${first ?? ''}${last ?? ''}`.toUpperCase();
  }
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default function Avatar({
  size = 48,
  src,
  alt = '',
  firstName,
  lastName,
  name,
  className,
}: IAvatarProps) {
  const initials = useMemo(
    () => initialsFromName(firstName, lastName, name),
    [firstName, lastName, name],
  );

  let sizeClass = styles.size48;
  if (size === 32) sizeClass = styles.size32;
  if (size === 72) sizeClass = styles.size72;

  if (src) {
    return (
      <img
        className={clsx(styles.avatar, sizeClass, styles.image, className)}
        src={src}
        alt={alt || name || 'Profile'}
        width={size}
        height={size}
      />
    );
  }

  return (
    <div
      className={clsx(styles.avatar, sizeClass, styles.initials, className)}
      role="img"
      aria-label={alt || name || initials}
    >
      <span>{initials}</span>
    </div>
  );
}
